import React, { useContext, useEffect, useState } from "react";
import { UserContext } from "../contexts/userContext";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase-config";
import {
  BUSINESS_COLLECTION,
  INGREDIENTS_COLLECTION,
} from "../utils/constants";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ModalConfirmation from "../components/ModalConfirmation";
import { IIngredient } from "../interfaces";
import CreateIngredientModal from "../components/CreateIngredientModal";
import { getFormattedDate } from "../utils/utils";

const Ingredients = () => {
  const { businessData } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [selectedIngredient, setSelectedIngredient] = useState(
    {} as IIngredient
  );
  const [list, setList] = useState<any>([] as IIngredient[]);
  const [showNewModal, setShowNewModal] = useState(false);
  const [page, setPage] = useState(0);
  const [lastVisible, setLastVisible] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [openModal, setOpenModal] = useState(
    {} as { show: boolean; type: "delete" | "disable" | "" }
  );

  const pageSize = 10;

  const handleClose = () => {
    setShowNewModal(false);
    setOpenModal({ show: false, type: "" });
    setSelectedIngredient({} as IIngredient);
  };

  const getList = async () => {
    const subCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      INGREDIENTS_COLLECTION
    );
    setLoading(true);
    let q;
    if (page === 0) {
      q = query(
        subCollectionRef,
        orderBy("createdAt", "desc"),
        limit(pageSize)
      );
    } else {
      q = query(
        subCollectionRef,
        orderBy("createdAt", "desc"),
        startAfter(lastVisible),
        limit(pageSize)
      );
    }
    const { docs } = await getDocs(q).finally(() => setLoading(false));
    if (docs.length > 0) {
      setList(docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      setLastVisible(docs[docs.length - 1] as any);
    } else setList([]);
  };

  const updateItem = async (item: any) => {
    const subCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      INGREDIENTS_COLLECTION
    );
    const ingredientDoc = doc(subCollectionRef, item.id);
    await updateDoc(ingredientDoc, item as any).then(() => {
      getList();
      setSelectedIngredient({} as IIngredient);
      handleClose();
    });
  };

  const handleDelete = async () => {
    setLoading(true);
    const ingredientDoc = doc(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      INGREDIENTS_COLLECTION,
      selectedIngredient.id
    );
    await deleteDoc(ingredientDoc)
      .then(() => getList())
      .finally(() => handleClose());
  };

  const handleSwitchStatus = () => {
    const itemToUpdate = {
      ...selectedIngredient,
      status: selectedIngredient.status === "enabled" ? "disabled" : "enabled",
    };
    updateItem(itemToUpdate);
    getList();
    handleClose();
  };

  const handleDeleteOrSwitch = () => {
    switch (openModal.type) {
      case "delete":
        return handleDelete();
      case "disable":
        return handleSwitchStatus();
      default:
        return null;
    }
  };

  const openProductModal = () => setShowNewModal(true);

  const handleChangePage = (_: any, newPage: any) => {
    setPage(newPage);
  };

  const getUnitLabel: { [key: string]: string } = {
    lt: "Litros",
    kg: "Kilogramos",
    piece: "Unidades",
  };

  useEffect(() => {
    if (businessData.uid) getList();
  }, [page, businessData]);

  // useEffect para obtener el conteo total de documentos para la paginación
  useEffect(() => {
    if (businessData.uid) {
      const ingredientsCollectionRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        INGREDIENTS_COLLECTION
      );
      setLoading(true);
      const fetchTotalCount = async () => {
        // Nota: Esta es una consulta costosa, hazla solo una vez si es posible
        const totalDocs = await getDocs(query(ingredientsCollectionRef));
        const list = totalDocs.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          createdAt: getFormattedDate(doc.data().createdAt),
          updatedAt: getFormattedDate(doc.data().updatedAt),
        })) as IIngredient[];
        localStorage.setItem("ingredients", JSON.stringify(list));

        setTotalItems(totalDocs.size);
      };
      fetchTotalCount();
    }
  }, [businessData]);
  return (
    <Grid
      container
      sx={{ width: "100%" }}
      display="flex"
      spacing={3}
      p={3}
      mt={10}
    >
      <Grid size={12} display="flex" alignItems="center">
        <Grid size={{ xs: 8, md: 10 }}>
          <Typography fontSize={24}>Ingredientes</Typography>
        </Grid>
        <Grid size={{ xs: 4, md: 2 }} justifyContent="end">
          <Button
            variant="contained"
            fullWidth
            size="small"
            color="secondary"
            onClick={openProductModal}
          >
            Nuevo ingrediente
          </Button>
        </Grid>
      </Grid>
      <Grid size={12} display="flex" justifyContent="center">
        {loading ? (
          <CircularProgress size={120} sx={{ mt: 4 }} />
        ) : (
          <>
            {list.length > 0 ? (
              <TableContainer component={Paper}>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Unidad</TableCell>
                      <TableCell>Cantidad</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.map((product: IIngredient) => (
                      <TableRow key={product.id}>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>{getUnitLabel[product.unit]}</TableCell>
                        <TableCell>{product.stock_quantity}</TableCell>
                        <TableCell align="center">
                          <Chip
                            color={
                              product.status === "enabled"
                                ? "success"
                                : "warning"
                            }
                            label={
                              product.status === "enabled"
                                ? "Habilitada"
                                : "Deshabilitada"
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton
                              onClick={() => {
                                setSelectedIngredient(product);
                                openProductModal();
                              }}
                            >
                              <span className="material-symbols-rounded">
                                edit
                              </span>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              color="error"
                              onClick={() => {
                                setSelectedIngredient(product);
                                setOpenModal({ show: true, type: "delete" });
                              }}
                            >
                              <span className="material-symbols-rounded">
                                delete
                              </span>
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              product.status === "enabled"
                                ? "Deshabilitar"
                                : "Habilitar"
                            }
                          >
                            <Switch
                              size="small"
                              color="secondary"
                              checked={product.status === "enabled"}
                              onChange={() => {
                                setSelectedIngredient(product);
                                setOpenModal({ show: true, type: "disable" });
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[pageSize]}
                  rowsPerPage={pageSize}
                  component="div"
                  page={page}
                  count={totalItems}
                  onPageChange={handleChangePage}
                />
              </TableContainer>
            ) : (
              <Alert severity="warning">No hay ingredientes cargados.</Alert>
            )}
          </>
        )}
      </Grid>

      {openModal.show && (
        <ModalConfirmation
          page="ingredient"
          status={selectedIngredient.status}
          openModal={openModal}
          handleClose={handleClose}
          handleSubmit={handleDeleteOrSwitch}
        />
      )}
      {showNewModal && (
        <CreateIngredientModal
          show={showNewModal}
          ingredient={selectedIngredient}
          handleClose={handleClose}
          updateItem={updateItem}
          getList={getList}
        />
      )}
    </Grid>
  );
};

export default Ingredients;
